/*
 * Tiny i18n for the P3 apps. Flat keys ("chat.send"), {placeholders}, plural keys with
 * CLDR suffixes ("log.messages_one" / "_other" / "_few" …). English ships in the bundle;
 * other locales load on demand. src/i18n/locales/en.json is the only hand-edited file —
 * scripts/translate-locales.mjs fills the rest.
 */
import en from './locales/en.json';
import { CODES, languageOf } from './languages';

export { LANGUAGES, languageOf } from './languages';

const STORE_KEY = 'xcards_locale';
let current = 'en';
let dict = en;

/** Maps stored, legacy and browser codes onto the catalog: zh/zh-CN → zh-Hans, zh-TW → zh-Hant, pt-BR → pt, tl → fil. */
export function normalizeLocale(code) {
  const c = String(code || '').trim().replace('_', '-');
  if (!c) return 'en';
  if (CODES.includes(c)) return c;
  const lower = c.toLowerCase();
  if (/^zh-(tw|hk|mo|hant)/.test(lower)) return 'zh-Hant';
  if (lower === 'zh' || lower.startsWith('zh-')) return 'zh-Hans';
  const base = lower.split('-')[0];
  if (base === 'tl') return 'fil';
  return CODES.includes(base) ? base : 'en';
}

export function initialLocale() {
  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved) return normalizeLocale(saved);
  } catch {
    /* storage blocked: fall through to the browser language */
  }
  return normalizeLocale(typeof navigator !== 'undefined' ? navigator.language : 'en');
}

/** Loads a locale's strings and makes it current. Missing keys fall back to English. */
export async function loadLocale(code) {
  const next = normalizeLocale(code);
  dict = next === 'en' ? en : (await import(`./locales/${next}.json`)).default;
  current = next;
  try {
    localStorage.setItem(STORE_KEY, next);
  } catch {
    /* the choice just won't persist */
  }
  return next;
}

export const locale = () => current;
export const dir = () => languageOf(current).dir;

const numberFmt = {};
export const formatNumber = (n, loc = current) => {
  if (!numberFmt[loc]) numberFmt[loc] = new Intl.NumberFormat(loc);
  return numberFmt[loc].format(n);
};

/** t('log.messages', { count: 3 }) → "3 messages"; numbers in vars are locale-formatted. */
export function t(key, vars = {}, d = dict) {
  let text;
  if (typeof vars.count === 'number') {
    const cat = new Intl.PluralRules(d === en ? 'en' : current).select(vars.count);
    text = d[`${key}_${cat}`] ?? d[`${key}_other`] ?? en[`${key}_${cat}`] ?? en[`${key}_other`];
  }
  text = text ?? d[key] ?? en[key] ?? key;
  return text.replace(/\{(\w+)\}/g, (m, name) => {
    const v = vars[name];
    if (v === undefined || v === null) return m;
    return typeof v === 'number' ? formatNumber(v) : String(v);
  });
}

/** English text for a key — for values sent to the AI or stored, which stay language-neutral. */
export const tEn = (key, vars) => t(key, vars, en);

/** Locale-aware date: formatDate('2026-10-20', { weekday: 'short', month: 'short', day: 'numeric' }). */
export function formatDate(value, opts = { weekday: 'short', month: 'short', day: 'numeric' }, loc = current) {
  const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00`) : new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(loc, opts);
}

export const formatDateTime = (value, loc = current) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
};

/** "Today" / "Tomorrow" / "Yesterday" in the current language, else a short date. */
export function relativeDay(diffDays, iso, loc = current) {
  if (Math.abs(diffDays) <= 1) {
    const s = new Intl.RelativeTimeFormat(loc, { numeric: 'auto' }).format(diffDays, 'day');
    return s.charAt(0).toLocaleUpperCase(loc) + s.slice(1);
  }
  return formatDate(iso, undefined, loc);
}

/** A language's name in the current UI language ("Japanese", "日语", "Japonais"…). */
export function languageName(code, loc = current) {
  try {
    return new Intl.DisplayNames([loc], { type: 'language' }).of(code) || code;
  } catch {
    return languageOf(code).name;
  }
}
